import React, { useEffect, useMemo, useState, useContext } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import {
  Button,
  Form,
  InputGroup,
  Pagination,
  Spinner,
  Badge,
  Modal,
} from "react-bootstrap";
import { AuthContext } from "../../context/AuthContext";
import {
  getMyApprovalQueue,
  mgrApprove,
  mgrRequestChanges,
  mgrReject,
  finApprove,
  markPaid,
} from "../../api";
import "../admins/AdminUnitPage.css";

const formatAmount = (amount) => {
  return `₹${Number(amount || 0).toFixed(2)}`;
};

const normalizeStatus = (status) => {
  return String(status || "-").trim().toUpperCase();
};

const titleCase = (value) => {
  return String(value || "-")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.slice(1));
};

export default function MyApprovals() {
  const { user } = useContext(AuthContext) || {};

  const currentUserId = user?.id;

  const [role, setRole] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentAction, setCommentAction] = useState(null);
  const [commentRow, setCommentRow] = useState(null);
  const [comment, setComment] = useState("");

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentRow, setPaymentRow] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    approved_amount: "",
    payment_mode: "NEFT",
    reference_no: "",
    remarks: "",
  });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, rowsPerPage]);

  const isManager = role === "MANAGER";
  const isFinance = role === "FINANCE";
  const isAdmin = role === "ADMIN";

  async function load() {
    if (!currentUserId) {
      setLoading(false);
      setError("User not found. Please login again.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const { data } = await getMyApprovalQueue(currentUserId);

      setRole(data?.role || null);
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      console.error("Approval Queue Error:", err);
      setError(err?.response?.data?.error || "Failed to load approval queue.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(id) {
    if (!currentUserId) return;

    try {
      setSavingId(id);

      if (isManager || isAdmin) {
        await mgrApprove(id, { actor_id: currentUserId });
      } else if (isFinance) {
        await finApprove(id, { actor_id: currentUserId });
      }

      await load();
    } catch (err) {
      console.error("Approve Error:", err);
      alert(err?.response?.data?.error || "Failed to approve request.");
    } finally {
      setSavingId(null);
    }
  }

  function openRequestChanges(row) {
    setCommentRow(row);
    setCommentAction("REQUEST_CHANGES");
    setComment("");
    setShowCommentModal(true);
  }

  function openReject(row) {
    setCommentRow(row);
    setCommentAction("REJECT");
    setComment("");
    setShowCommentModal(true);
  }

  async function submitCommentAction() {
    if (!commentRow || !currentUserId) return;

    if (commentAction === "REQUEST_CHANGES" && !comment.trim()) {
      alert("Please enter comment for requester.");
      return;
    }

    try {
      setSavingId(commentRow.id);

      if (commentAction === "REQUEST_CHANGES") {
        await mgrRequestChanges(commentRow.id, {
          actor_id: currentUserId,
          comment: comment.trim(),
        });
      }

      if (commentAction === "REJECT") {
        await mgrReject(commentRow.id, {
          actor_id: currentUserId,
          comment: comment.trim(),
        });
      }

      setShowCommentModal(false);
      setCommentRow(null);
      setCommentAction(null);
      setComment("");

      await load();
    } catch (err) {
      console.error("Comment Action Error:", err);
      alert(err?.response?.data?.error || "Failed to submit action.");
    } finally {
      setSavingId(null);
    }
  }

  function openPayment(row) {
    setPaymentRow(row);
    setPaymentForm({
      approved_amount: row?.amount ? String(row.amount) : "",
      payment_mode: "NEFT",
      reference_no: "",
      remarks: "",
    });
    setShowPaymentModal(true);
  }

  async function submitPayment() {
    if (!paymentRow || !currentUserId) return;

    const approvedAmount = Number(paymentForm.approved_amount || 0);

    if (!approvedAmount) {
      alert("Please enter approved amount.");
      return;
    }

    try {
      setSavingId(paymentRow.id);

      await markPaid(paymentRow.id, {
        actor_id: currentUserId,
        approved_amount: approvedAmount,
        payment_mode: paymentForm.payment_mode || "NEFT",
        reference_no: paymentForm.reference_no || null,
        remarks: paymentForm.remarks || null,
      });

      setShowPaymentModal(false);
      setPaymentRow(null);

      await load();
    } catch (err) {
      console.error("Payment Error:", err);
      alert(err?.response?.data?.error || "Failed to mark payment.");
    } finally {
      setSavingId(null);
    }
  }

  const columnsHelp = useMemo(() => {
    if (isManager || isAdmin) return "You can approve, request changes or reject requests.";
    if (isFinance) return "You can approve for payment and mark requests as paid.";
    return "Approval actions will appear based on your assigned role.";
  }, [isManager, isAdmin, isFinance]);

  const statusOptions = useMemo(() => {
    const unique = new Set(rows.map((row) => normalizeStatus(row.status_code)));
    return Array.from(unique).filter(Boolean);
  }, [rows]);

  const stats = useMemo(() => {
    const total = rows.length;
    const totalAmount = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);

    const pending = rows.filter((row) => {
      const status = normalizeStatus(row.status_code);
      return status.includes("PENDING") || status.includes("WAITING");
    }).length;

    const approved = rows.filter((row) => {
      const status = normalizeStatus(row.status_code);
      return status.includes("APPROVED");
    }).length;

    const paid = rows.filter((row) => {
      const status = normalizeStatus(row.status_code);
      return status.includes("PAID");
    }).length;

    return {
      total,
      pending,
      approved,
      paid,
      totalAmount,
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return rows.filter((row) => {
      const status = normalizeStatus(row.status_code);

      const passStatus = statusFilter === "ALL" ? true : status === statusFilter;

      if (!q) return passStatus;

      const haystack = [
        row.id,
        row.title,
        row.amount,
        row.status_code,
        row.requester_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return passStatus && haystack.includes(q);
    });
  }, [rows, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const pageData = filteredRows.slice(indexOfFirst, indexOfLast);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
    setCurrentPage(1);
  };

  const getStatusClass = (statusCode) => {
    const status = normalizeStatus(statusCode);

    if (status.includes("PAID") || status.includes("APPROVED")) {
      return "unit-status unit-status-returned";
    }

    if (status.includes("REJECT")) {
      return "unit-status unit-status-cancelled";
    }

    if (status.includes("PENDING") || status.includes("WAITING")) {
      return "unit-status unit-status-active";
    }

    return "unit-status";
  };

  return (
    <div className="unit-page approvals-theme">
      <div className="unit-header">
        <div>
          <h1>
            My Approvals{" "}
            {role ? <small className="text-muted">({role})</small> : null}
          </h1>
          <p>{columnsHelp}</p>
        </div>

        <div className="unit-header-actions">
          <Button className="unit-btn unit-btn-light" onClick={load} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
      </div>

      <div className="unit-stats-row unit-top-stats approvals-stat-row">
        <div className="unit-stat-card">
          <span>Total Requests</span>
          <strong>{stats.total}</strong>
          <small>Approval queue items</small>
        </div>

        <div className="unit-stat-card">
          <span>Pending</span>
          <strong>{stats.pending}</strong>
          <small>Waiting for action</small>
        </div>

        <div className="unit-stat-card">
          <span>Approved</span>
          <strong>{stats.approved}</strong>
          <small>Approved requests</small>
        </div>

        <div className="unit-stat-card">
          <span>Paid</span>
          <strong>{stats.paid}</strong>
          <small>Marked as paid</small>
        </div>

        <div className="unit-stat-card">
          <span>Total Amount</span>
          <strong style={{ fontSize: "22px" }}>{formatAmount(stats.totalAmount)}</strong>
          <small>Visible queue amount</small>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h2>Approval Queue</h2>
            <p>Search, filter and take approval actions from one place.</p>
          </div>

          <div className="unit-toolbar-actions">
            <InputGroup className="unit-search approvals-search">
              <InputGroup.Text>⌕</InputGroup.Text>

              <Form.Control
                placeholder="Search title, requester, status, amount..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />

              {search && (
                <Button className="unit-clear-btn" onClick={() => setSearch("")}>
                  Clear
                </Button>
              )}
            </InputGroup>

            <Form.Select
              className="unit-page-size"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="ALL">All Status</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {titleCase(status)}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size"
              value={rowsPerPage}
              onChange={(event) => setRowsPerPage(Number(event.target.value))}
            >
              <option value={5}>5 rows</option>
              <option value={10}>10 rows</option>
              <option value={20}>20 rows</option>
              <option value={50}>50 rows</option>
            </Form.Select>

            {(search || statusFilter !== "ALL") && (
              <Button className="unit-btn unit-btn-light" onClick={clearFilters}>
                Reset
              </Button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-danger mx-3">{error}</div>}

        <div className="unit-table-wrapper">
          {loading ? (
            <div className="unit-loader">
              <Spinner animation="border" />
              <p>Loading approvals...</p>
            </div>
          ) : (
            <table className="table unit-table">
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>ID</th>
                  <th>Title</th>
                  <th className="text-end">Amount</th>
                  <th>Status</th>
                  <th>Requester</th>
                  <th className="text-end" style={{ width: "390px" }}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {pageData.length > 0 ? (
                  pageData.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <span className="unit-id">#{row.id}</span>
                      </td>

                      <td>
                        <div className="unit-name-box">
                          <div className="unit-letter">
                            {row.title?.charAt(0)?.toUpperCase() || "A"}
                          </div>

                          <div>
                            <strong>{row.title || "-"}</strong>
                            <small>Approval request</small>
                          </div>
                        </div>
                      </td>

                      <td className="text-end">
                        <strong>{formatAmount(row.amount)}</strong>
                      </td>

                      <td>
                        <Badge className={getStatusClass(row.status_code)}>
                          {titleCase(row.status_code)}
                        </Badge>
                      </td>

                      <td>
                        <Badge className="unit-status">
                          {row.requester_name || "-"}
                        </Badge>
                      </td>

                      <td>
                        <div className="unit-actions justify-content-end approvals-actions">
                          {(isManager || isFinance || isAdmin) && (
                            <Button
                              className="unit-icon-btn unit-edit"
                              title="Approve"
                              onClick={() => handleApprove(row.id)}
                              disabled={savingId === row.id}
                            >
                              ✓
                            </Button>
                          )}

                          {(isManager || isAdmin) && (
                            <>
                              <Button
                                className="unit-icon-btn unit-edit"
                                title="Request Changes"
                                onClick={() => openRequestChanges(row)}
                                disabled={savingId === row.id}
                              >
                                ↺
                              </Button>

                              <Button
                                className="unit-icon-btn unit-delete"
                                title="Reject"
                                onClick={() => openReject(row)}
                                disabled={savingId === row.id}
                              >
                                ×
                              </Button>
                            </>
                          )}

                          {isFinance && (
                            <Button
                              className="unit-icon-btn unit-edit"
                              title="Mark Paid"
                              onClick={() => openPayment(row)}
                              disabled={savingId === row.id}
                            >
                              ₹
                            </Button>
                          )}

                          <a
                            className="unit-btn unit-btn-light approvals-view-btn"
                            href={`/admin/accounts/reports/${row.id}`}
                          >
                            View
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">
                      <div className="unit-empty">
                        <div className="unit-empty-icon">✓</div>
                        <h3>Nothing to approve</h3>
                        <p>Your approval queue is clear.</p>

                        <Button className="unit-btn unit-btn-light" onClick={load}>
                          Refresh
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">
            Showing{" "}
            <strong>{filteredRows.length === 0 ? 0 : indexOfFirst + 1}</strong>{" "}
            to <strong>{Math.min(indexOfLast, filteredRows.length)}</strong> of{" "}
            <strong>{filteredRows.length}</strong> entries
          </div>

          {totalPages > 1 && (
            <Pagination className="unit-pagination">
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              />

              {compactPageItems([...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={index + 1 === currentPage}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              )), currentPage)}

              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              />
            </Pagination>
          )}
        </div>
      </div>

      <Modal
        show={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        centered
        className="unit-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {commentAction === "REQUEST_CHANGES"
              ? "Request Changes"
              : "Reject Request"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="alert alert-light border">
            <strong>{commentRow?.title || "-"}</strong>
            <div className="text-muted small mt-1">
              Amount: {formatAmount(commentRow?.amount)}
            </div>
          </div>

          <Form.Group>
            <Form.Label>
              Comment{" "}
              {commentAction === "REQUEST_CHANGES" ? (
                <span className="text-danger">*</span>
              ) : null}
            </Form.Label>

            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter comment..."
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light"
            onClick={() => setShowCommentModal(false)}
            disabled={savingId === commentRow?.id}
          >
            Cancel
          </Button>

          <Button
            className={
              commentAction === "REJECT"
                ? "unit-btn unit-btn-light"
                : "unit-btn unit-btn-primary"
            }
            onClick={submitCommentAction}
            disabled={savingId === commentRow?.id}
          >
            {savingId === commentRow?.id ? "Submitting..." : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        centered
        className="unit-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Mark Paid</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="alert alert-light border">
            <strong>{paymentRow?.title || "-"}</strong>
            <div className="text-muted small mt-1">
              Requested Amount: {formatAmount(paymentRow?.amount)}
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Approved Amount <span className="text-danger">*</span>
                </Form.Label>

                <Form.Control
                  type="number"
                  min="0"
                  value={paymentForm.approved_amount}
                  onChange={(event) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      approved_amount: event.target.value,
                    }))
                  }
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Payment Mode</Form.Label>

                <Form.Select
                  value={paymentForm.payment_mode}
                  onChange={(event) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      payment_mode: event.target.value,
                    }))
                  }
                >
                  <option value="NEFT">NEFT</option>
                  <option value="UPI">UPI</option>
                  <option value="CASH">CASH</option>
                  <option value="CHEQUE">CHEQUE</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Reference No</Form.Label>

                <Form.Control
                  value={paymentForm.reference_no}
                  onChange={(event) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      reference_no: event.target.value,
                    }))
                  }
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Remarks</Form.Label>

                <Form.Control
                  value={paymentForm.remarks}
                  onChange={(event) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      remarks: event.target.value,
                    }))
                  }
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light"
            onClick={() => setShowPaymentModal(false)}
            disabled={savingId === paymentRow?.id}
          >
            Cancel
          </Button>

          <Button
            className="unit-btn unit-btn-primary"
            onClick={submitPayment}
            disabled={savingId === paymentRow?.id}
          >
            {savingId === paymentRow?.id ? "Saving..." : "Mark Paid"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .approvals-theme .unit-header {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px 24px 20px;
          box-shadow: 0 1px 8px rgba(15, 23, 42, 0.04);
          margin-bottom: 20px;
        }

        .approvals-theme .approvals-stat-row {
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        }

        .approvals-theme .unit-stat-card small {
          display: block;
          margin-top: 6px;
          color: #64748b;
          font-size: 11px;
          font-weight: 700;
        }

        .approvals-theme .approvals-search {
          min-width: 340px;
        }

        .approvals-theme .unit-table thead th {
          background: var(--primary, #15803d) !important;
          color: #ffffff !important;
          border-color: var(--primary, #15803d) !important;
        }

        .approvals-theme .approvals-actions {
          flex-wrap: nowrap;
        }

        .approvals-theme .approvals-view-btn {
          padding: 8px 12px;
          font-size: 12px;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        @media (max-width: 768px) {
          .approvals-theme .approvals-search,
          .approvals-theme .unit-page-size {
            min-width: 100%;
          }

          .approvals-theme .approvals-actions {
            flex-wrap: wrap;
            justify-content: flex-start !important;
          }
        }
      `}</style>
    </div>
  );
}
